import type { ReactNode } from 'react';
import { Layout, useSidebarState } from 'react-admin';
import CustomMenu from './CustomMenu';
import CustomAppBar from './CustomAppBar';
import { useDefaultDepartmentView } from './departmentView';
import { EnvironmentBanner } from './EnvironmentBanner';
import { FeatureFlagsReady, FeatureGate } from './featureFlags/FeatureGate';
import { GuideOverlay } from '../guide/GuideOverlay';
import { AutoListFilters } from './listMemory/AutoListFilters';
import { UsageTracker } from '../usage/UsageTracker';
import { MockupLookTheme } from '../mockupLook/MockupLookTheme';
import { CONTENT_MAX_WIDTH, CONTENT_NO_GROW_SX, CONTENT_PADDING_X, DATAGRID_WRAPPER_SX, LAYOUT_NO_GROW_SX } from './layoutWidth';
import { useAutoCollapseSidebar } from './useNarrowLayout';
import { ImpersonationBanner } from '../session/ImpersonationBanner';
import { OtherTabImpersonationNotice } from '../session/OtherTabImpersonationNotice';

/**
 * Układ panelu: wąski panel boczny plus własny pasek górny.
 *
 * Port z FreightFusion (`admin/CustomLayout.jsx`, stan z 15.09.2026).
 * Zapis pochodzenia: PORT-FROM-TLSAPP.md
 *
 * Nadpisania `sx` przeniesione ze źródła bez zmian — react-admin domyślnie
 * daje panel szerszy i z marginesami, przez co listy z wieloma kolumnami
 * (a takie są w banku zleceń) nie mieszczą się na ekranie.
 *
 * `ImpersonationBanner` ze źródła wrócił 02.11.2026 w nowym kształcie
 * („Zaloguj jako”, decyzja Bartka, `session/ImpersonationBanner.tsx`).
 */

const SIDEBAR_WIDTH_OPEN = 180;
const SIDEBAR_WIDTH_CLOSED = 47;

/**
 * Pasek boczny przewija się z widocznym, wąskim paskiem przewijania
 * (audyt układu stagingu 28.10.2026: przy 1366×768 ostatnia pozycja menu —
 * „Przewoźnicy” — była ucięta dolną krawędzią okna). react-admin przewija
 * kontener `RaSidebar-fixed`, ale chowa pasek przewijania, więc nic nie
 * mówiło, że niżej jest jeszcze coś. Pasek pojawia się tylko wtedy, gdy menu
 * jest wyższe od okna — przy większych ekranach wygląd się nie zmienia.
 */
export const SIDEBAR_SCROLL_SX = {
  overflowY: 'auto !important',
  overscrollBehavior: 'contain',
  scrollbarWidth: 'thin !important',
  msOverflowStyle: 'auto !important',
  '&::-webkit-scrollbar': { display: 'block !important', width: 6 },
  '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0, 0, 0, 0.25)', borderRadius: 3 },
} as const;

export const CustomLayout = ({ children }: { children: ReactNode }) => {
  const [sidebarOpen] = useSidebarState();
  // Trader zaczyna od widoku Trade, planista FTL od FTL (01.10.2026).
  useDefaultDepartmentView();
  // Poniżej 1280 px menu zwinięte do ikon (29.10.2026, `useNarrowLayout.ts`).
  useAutoCollapseSidebar();
  const sidebarWidth = sidebarOpen ? SIDEBAR_WIDTH_OPEN : SIDEBAR_WIDTH_CLOSED;

  return (
    // Wygląd z makiety (28.10.2026, S-28.10-01, przełącznik `mockupLook`) —
    // motyw z makiety nad całym układem: pasek, menu, ekrany i okna.
    // Wyłączony: motyw jak dotąd (`mockupLook/MockupLookTheme.tsx`).
    <MockupLookTheme>
      <Layout
        menu={CustomMenu}
        appBar={CustomAppBar}
        sx={{
          '& .RaSidebar-fixed': {
            width: `${sidebarWidth}px !important`,
            minWidth: `${sidebarWidth}px !important`,
            left: '0 !important',
            ...SIDEBAR_SCROLL_SX,
          },
          '& .RaSidebar-paper': {
            width: `${sidebarWidth}px !important`,
            minWidth: `${sidebarWidth}px !important`,
            padding: '0 !important',
          },
          '& .MuiDrawer-paper': {
            width: `${sidebarWidth}px !important`,
            minWidth: `${sidebarWidth}px !important`,
            padding: '0 !important',
            left: '0 !important',
          },
          '& .RaLayout-content': {
            marginLeft: '0 !important',
            paddingLeft: `${CONTENT_PADDING_X}px !important`,
            ...CONTENT_NO_GROW_SX,
          },
          // Układ nie rośnie ponad okno (29.10.2026) — szeroki element w środku przewija się
          // u siebie, zamiast rozpychać stronę w bok (`layoutWidth.ts`).
          ...LAYOUT_NO_GROW_SX,
          // Tabele list react-admina przewijają się w swoim opakowaniu, a opakowanie nie zgłasza
          // rodzicom szerokości tabeli (`width: 0` + `minWidth: 100%`, ten sam wzór co bank
          // zleceń) — inaczej przy 1024 px lista spółek, kontrahentów albo dziennik działań
          // rozpychała całą stronę w bok (przegląd wyglądu na różnych rozdzielczościach 29.10.2026).
          '& .RaDatagrid-tableWrapper': DATAGRID_WRAPPER_SX,
          '& .RaLayout-contentWithSidebar': { marginLeft: '0 !important' },
          '& main': { marginLeft: '0 !important', paddingLeft: '0 !important' },
          '& .RaMenu-open': { width: `${sidebarWidth}px !important` },
          '& .RaMenu-closed': { width: `${SIDEBAR_WIDTH_CLOSED}px !important` },
        }}
      >
        {/* Ograniczenie szerokości trzyma szerokie listy w kadrze zamiast
            pozwalać im rozpychać stronę w poziomie. `wsp-tresc` — treść ekranu
            z kontraktu `data-testid` paczki (S-22.10-02). */}
        <div style={{ maxWidth: CONTENT_MAX_WIDTH }} data-testid="wsp-tresc">
          {/* „Zaloguj jako” (02.11.2026): stały pasek w karcie przejęcia —
              kto pracuje i kto się zalogował, „Zakończ”. Poza nią nic. */}
          <ImpersonationBanner />
          {/* Karta bez sesji przejęcia przy przejęciu w innej karcie — ostrzeżenie. */}
          <OtherTabImpersonationNotice />
          {/* Pasek „Środowisko akceptacyjne” — tylko w paczce stagingu
              (12.10.2026); na dev i prod komponent nic nie rysuje. */}
          <EnvironmentBanner />
          {/* Pomiar użycia (22.10.2026, S-22.10-02) — bez nic na ekranie;
              zbiera tylko przy włączonym `usageTelemetry` w środowisku. */}
          <UsageTracker />
          {/* Ekran dopiero po pierwszym odczycie przełączników w sesji — bez
              mignięcia wersji „obecnej” przed „nową” (integracja przełączników
              12.10.2026); po błędzie odczytu — wersja „obecna”. Filtry list
              zapamiętywane same, bez „Zapisz obecne filtry” — za
              przełącznikiem `autoListFilters` (19.10.2026). */}
          <FeatureFlagsReady>
            <AutoListFilters>{children}</AutoListFilters>
            {/* Przewodnik po zmianach (21.10.2026, decyzja użytkownika na prośbę
                klienta) — przesuwalny panel prowadzący przez nowe funkcje; za
                przełącznikiem `guideOverlay` (S-21.10-28). */}
            <FeatureGate feature="guideOverlay">
              <GuideOverlay />
            </FeatureGate>
          </FeatureFlagsReady>
        </div>
      </Layout>
    </MockupLookTheme>
  );
};

export default CustomLayout;
